// Fachsprache und Aufruf der Namenspflege einer Person (ST-PTY-001,
// CM-PTY-002). REINES MODELL, kein React, kein DOM; die Antwort liest der
// Befehls-Client.

import type { PlainMessage } from './access.ts';
import { sendCommand, type CommandMessages, type CommandOutcome, type SendLike } from './command-client.ts';

export interface PersonNameApplied {
  readonly displayName: string;
  readonly givenName?: string;
  readonly familyName?: string;
  readonly lockVersion: number;
}

export const PERSON_NAME_MESSAGES: CommandMessages = Object.freeze({
  notFound: Object.freeze({
    headline: 'Diesen Kontakt gibt es hier nicht.',
    // Keine Vermutung ueber eine Berechtigung: der Server beantwortet beides gleich.
    explanation: 'Er wurde entfernt, oder die Adresse ist nicht mehr gültig.',
    nextStep: 'Geh zurück zur Kontaktliste und öffne den Kontakt dort erneut.',
    retryable: false,
  }),
  conflictFallback: Object.freeze({
    headline: 'Jemand anderes war schneller.',
    explanation: 'Der Name dieses Kontakts hat sich geändert, seit du die Seite geöffnet hast. Deine Eingabe wurde nicht gespeichert.',
    nextStep: 'Die Seite zeigt jetzt den aktuellen Namen. Prüfe ihn und ändere ihn bei Bedarf noch einmal.',
    retryable: false,
  }),
}) satisfies CommandMessages;

/** Der Satz zu einer abgewiesenen Eingabe, je Feld und Grund (NFR-UX-005). */
export function personNameValidationMessage(fieldPath: string, reasonCode: string): string {
  const feld = fieldPath === 'person.givenName' ? 'Der Vorname' : 'Der Nachname';
  if (reasonCode === 'field_too_long') {
    return `${feld} ist zu lang.`;
  }
  if (reasonCode === 'field_required') {
    return 'Trage mindestens einen Vor- oder Nachnamen ein.';
  }
  return `Prüfe ${feld === 'Der Vorname' ? 'den Vornamen' : 'den Nachnamen'}.`;
}

/** Die Bestaetigung nennt die Wirkung: unter welchem Namen der Kontakt ab jetzt in Listen und Suche steht. */
export function personNameConfirmation(applied: PersonNameApplied): string {
  return `In Listen und in der Suche erscheint der Kontakt als „${applied.displayName}“.`;
}

/** Ein Stand, den der Server nie fuehrt: die Antwort war unvollstaendig, bevor ein Befehl entsteht. */
export const UNKNOWN_LOCK_VERSION_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Der Name lässt sich gerade nicht ändern.',
  explanation: 'Die Angaben zu diesem Kontakt sind unvollständig angekommen.',
  nextStep: 'Lade die Seite neu und versuche es dann noch einmal.',
  retryable: true,
});

export async function renamePerson(options: {
  readonly baseUrl: string;
  readonly accessToken: string;
  readonly partyId: string;
  readonly givenName: string;
  readonly familyName: string;
  readonly lockVersion: number;
  readonly send?: SendLike;
}): Promise<CommandOutcome<PersonNameApplied>> {
  return sendCommand({
    url: `${options.baseUrl}/parties/${encodeURIComponent(options.partyId)}/person-name`,
    accessToken: options.accessToken,
    body: {
      person: { givenName: options.givenName, familyName: options.familyName },
      lockVersion: options.lockVersion,
    },
    messages: PERSON_NAME_MESSAGES,
    applied: (record) => {
      // Der gespeicherte Stand kommt aus der Antwort, nicht aus der Eingabe.
      const person = (record.person ?? {}) as Record<string, unknown>;
      return {
        displayName: typeof record.displayName === 'string' ? record.displayName : '',
        ...(typeof person.givenName === 'string' ? { givenName: person.givenName } : {}),
        ...(typeof person.familyName === 'string' ? { familyName: person.familyName } : {}),
        lockVersion: typeof person.lockVersion === 'number' ? person.lockVersion : 0,
      };
    },
    ...(options.send === undefined ? {} : { send: options.send }),
  });
}
